import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LawnDashboardProvider, useLawnDashboard } from './LawnDashboardContext';
import WidgetGrid from './WidgetGrid';
import PropertyView from './PropertyView';
import SensorDetail from './SensorDetail';
import ForecastWidget from './ForecastWidget';
import OrdinanceWidget from './OrdinanceWidget';
import SensorListWidget from './SensorListWidget';
import WidgetPicker from './WidgetPicker';
import { WIDGET_TYPE_LABELS } from '../../types/lawnTypes';
import '../../styles/lawn/lawnDashboard.css';

/**
 * Lawn Irrigation Dashboard page.
 *
 * A single customizable page: a property view where sensors are pinned, a
 * forecast strip, the local watering ordinance, and a sensor table — each one a
 * `lawn_dashboard_widgets` row the user can move, resize, or remove.
 */

function LawnDashboardBody() {
  const {
    status,
    loadError,
    apiError,
    isDemoData,
    property,
    sensors,
    forecast,
    ordinance,
    widgets,
    selectedSensor,
    selectedSensorId,
    readings,
    isLoadingReadings,
    isAddingSensor,
    reload,
    addSensor,
    updateSensor,
    removeSensor,
    selectSensor,
    saveWidgets,
    addWidget,
    removeWidget,
    setPropertyImage,
    resetDashboard,
  } = useLawnDashboard();

  const [isResetting, setIsResetting] = useState(false);

  const activeWidgetTypes = useMemo(
    () => widgets.map((widget) => widget.widgetType),
    [widgets]
  );

  const statusCounts = useMemo(
    () =>
      sensors.reduce(
        (counts, sensor) => ({ ...counts, [sensor.status]: (counts[sensor.status] || 0) + 1 }),
        {}
      ),
    [sensors]
  );

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await resetDashboard();
    } finally {
      setIsResetting(false);
    }
  };

  const renderWidget = (widget) => {
    switch (widget.widgetType) {
      case 'property_view':
        return (
          <PropertyView
            property={property}
            sensors={sensors}
            selectedSensorId={selectedSensorId}
            onSelectSensor={selectSensor}
            onAddSensor={addSensor}
            onChangeImage={setPropertyImage}
            isAddingSensor={isAddingSensor}
          />
        );
      case 'forecast':
        return <ForecastWidget forecast={forecast} isLoading={status === 'loading'} />;
      case 'ordinance':
        return (
          <OrdinanceWidget
            ordinance={ordinance}
            address={property?.address}
            isLoading={status === 'loading'}
          />
        );
      case 'sensor_detail':
        return (
          <SensorDetail
            sensor={selectedSensor}
            readings={readings}
            isLoadingReadings={isLoadingReadings}
            onRename={(sensorId, name) => updateSensor(sensorId, { name })}
            onRemove={removeSensor}
          />
        );
      case 'sensor_list':
        return (
          <SensorListWidget
            sensors={sensors}
            selectedSensorId={selectedSensorId}
            onSelectSensor={selectSensor}
          />
        );
      default:
        return <p className="lawn-widget-muted">Unknown widget type.</p>;
    }
  };

  // Titles and per-widget header controls live here so WidgetGrid stays generic.
  const decoratedWidgets = useMemo(
    () =>
      widgets.map((widget) => {
        let title = WIDGET_TYPE_LABELS[widget.widgetType] || 'Widget';
        if (widget.widgetType === 'property_view' && property?.name) {
          title = property.name;
        }
        if (widget.widgetType === 'sensor_detail') {
          title = selectedSensor ? `Sensor · ${selectedSensor.name}` : 'Sensor detail';
        }

        const headerAccessory =
          widget.widgetType === 'property_view' ? (
            <span className="lawn-widget-count">
              {sensors.length} {sensors.length === 1 ? 'sensor' : 'sensors'}
            </span>
          ) : null;

        return { ...widget, title, headerAccessory };
      }),
    [widgets, property, selectedSensor, sensors.length]
  );

  if (status === 'loading' && widgets.length === 0) {
    return (
      <div className="lawn-page">
        <p className="lawn-widget-muted">Loading lawn dashboard…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="lawn-page">
        <p className="lawn-page-error">{loadError || 'Could not load the lawn dashboard.'}</p>
        <button type="button" className="green-button" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="lawn-page">
      <header className="lawn-page-header">
        <div>
          <h1>{property?.name || 'Lawn irrigation'}</h1>
          <p className="lawn-page-address">{property?.address || 'No address on file'}</p>
          <p className="lawn-page-summary">
            {statusCounts.online || 0} online · {statusCounts.low_battery || 0} low battery ·{' '}
            {statusCounts.offline || 0} offline
          </p>
        </div>

        <div className="lawn-page-actions">
          <WidgetPicker activeWidgetTypes={activeWidgetTypes} onAddWidget={addWidget} />
          <button
            type="button"
            className="lawn-button-secondary"
            onClick={handleReset}
            disabled={isResetting}
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>{' '}
            {isResetting ? 'Resetting…' : 'Reset demo data'}
          </button>
          <Link className="lawn-page-back" to="/dashboard">
            Back to sensors
          </Link>
        </div>
      </header>

      {isDemoData && (
        <p className="lawn-page-notice">
          Showing local demo data{apiError ? ` (lawn API unavailable: ${apiError})` : ''}. Sensors
          and layout are saved in this browser only.
        </p>
      )}

      {widgets.length === 0 ? (
        <div className="lawn-page-blank">
          <p>Every widget has been removed. Use “Add widget” to rebuild the dashboard.</p>
        </div>
      ) : (
        <WidgetGrid
          widgets={decoratedWidgets}
          renderWidget={renderWidget}
          onLayoutChange={saveWidgets}
          onRemoveWidget={removeWidget}
        />
      )}

      <p className="lawn-page-footnote">
        Drag a widget header to move it, or drag its bottom-right corner to resize. With a header
        focused, arrow keys move and shift plus arrow keys resize.
      </p>
    </div>
  );
}

export default function LawnDashboard() {
  return (
    <LawnDashboardProvider>
      <LawnDashboardBody />
    </LawnDashboardProvider>
  );
}
